import { Div, ldb, router, IRouteEvent, emitter } from '@codesuma/baseline'
import { ListItem } from '../../components/list-item'
import { ThemeToggle } from '../../components/theme-toggle'
import { ListHeader } from './header'
import { ColorPanel } from './color-panel'
import { IconPanel } from './icon-panel'
import { AddBar } from './add-bar'
import { DoneDivider } from './done-divider'
import { createReorder } from './reorder'
import { applyOrder, doneLast, foldDrag, sortByOrder } from './order'
import { listToText } from './export'
import { toast } from '../../utils/toast'
import list, { Item } from '../../services/list'
import listsService from '../../services/lists'
import itemOrder from '../../services/item-order'
import services from '../../services'
import helpers from '../../helpers'
import { pickedIconUrl } from '../../configs/list-icons'
import { iconKeyForName } from '../../components/list-circle/icon-for-name'
import style from './index.module.css'

type Row = ReturnType<typeof ListItem>

export const ListPage = () => {
    const base = Div()
    base.addClass(style.base)

    let listId = ''
    let viewing = false      // true while this list page is the active route
    let animateNext = false  // animate rows in only on the first render after entering

    // Foreground: white text on night / dark hue on light, plus the button accent.
    const paintForeground = (id: string) => {
        base.style({ color: helpers.textForId(id) })
        base.el.style.setProperty('--accent', helpers.accentForId(id))
    }

    // Full instant repaint (used when the theme is flipped while this page is open —
    // no morph in flight, so the background snaps straight to the new fill).
    const applyPalette = (id: string) => {
        paintForeground(id)
        base.style({ backgroundColor: helpers.surfaceForId(id) })
    }

    // Theme flipped from the bottom toggle while this list is open — repaint it.
    emitter.on('theme', () => { if (viewing && listId) applyPalette(listId) })

    // A member recolored this list from their own device (Realtime, via
    // listsService.applyRemoteColor) — repaint it here too, in place.
    listsService.on('change', () => { if (viewing && listId) applyPalette(listId) })

    const header = ListHeader()

    const addBar = AddBar()
    addBar.on('add', ({ value }: { value: string }) => list.add(value))

    // "Add section" (⋮ menu): a heading divider row. It arrives at the top like
    // any new row — drag it (or the items) into place. prompt() works in both
    // WebViews; confirm() is already used the same way elsewhere on this page.
    header.on('add-section', () => {
        const name = prompt('Section name')?.trim()
        if (name) list.add(name, 'General', 'heading')
    })

    // Inline title rename → persist locally + to the server, and keep the
    // "reopen last list" record in step with the new name.
    header.on('rename', ({ name }: { name: string }) => {
        if (!listId) return
        listsService.renameList(listId, name)
        ldb.set('list:last', { id: listId, name })
    })

    // Color picker sheet (from the ⋮ menu). Body-level overlay like the share
    // panel; picking repaints this page in place and persists via the service
    // (which also updates the home circle through the helpers override).
    const colorPanel = ColorPanel()
    colorPanel.on('close', () => {
        if (colorPanel.el.parentNode) colorPanel.el.parentNode.removeChild(colorPanel.el)
    })
    colorPanel.on('pick', ({ index }: { index: number }) => {
        if (!listId) return
        listsService.setColor(listId, index)
        applyPalette(listId)
    })
    header.on('color', () => {
        if (!listId) return
        document.body.appendChild(colorPanel.el)
        colorPanel.show(listId)
    })

    // Icon picker sheet (from the ⋮ menu). It opens on the icon the home circle
    // shows: the picked one, or else the one guessed from the name. This page
    // doesn't show the icon, so a toast confirms the pick took.
    const iconPanel = IconPanel()
    iconPanel.on('close', () => {
        if (iconPanel.el.parentNode) iconPanel.el.parentNode.removeChild(iconPanel.el)
    })
    iconPanel.on('pick', (key: string) => {
        if (!listId) return
        listsService.setIcon(listId, key)
        toast('Icon changed')
    })
    header.on('icon', () => {
        if (!listId) return
        const cached = listsService.getCached(listId)
        const picked = cached?.icon && pickedIconUrl(cached.icon) ? cached.icon : null
        document.body.appendChild(iconPanel.el)
        iconPanel.show(picked ?? iconKeyForName(cached?.name || ''))
    })

    // Copy the whole list as plain text (⋮ menu) to paste into a chat. Uses the
    // on-screen order, so sections and the done zone come out as they look here.
    header.on('copy', async () => {
        if (!listId) return
        const items = applyOrder(listId, list.getAll())
        if (!items.length) return toast('Nothing to copy yet')
        const name = listsService.getCachedName(listId) || 'List'
        try {
            await navigator.clipboard.writeText(listToText(name, items))
            toast('List copied')
        } catch (err) {
            console.error('Failed to copy the list:', err)
            toast("Couldn't copy the list")
        }
    })

    // Pin/unpin from the ⋮ menu — a per-device home-grid ordering choice.
    header.on('pin', () => {
        if (!listId) return
        const next = !listsService.isPinned(listId)
        listsService.setPinned(listId, next)
        header.setPinned(next)
    })

    // Delete (owner) or leave (member) the list, then return to the grid.
    // The server applies the same distinction (see api delete-list).
    header.on('delete', async () => {
        if (!listId) return
        const name = listsService.getCachedName(listId) || 'this list'
        const isMember = listsService.getCached(listId)?.role === 'member'
        const msg = isMember
            ? `Leave "${name}"? The list stays available to its other members.`
            : `Delete "${name}" and all its items?`
        if (!confirm(msg)) return
        await listsService.deleteList(listId)
        router.goto('/')
    })

    const listEl = Div()
    listEl.addClass(style.list)

    const empty = Div()
    empty.addClass(style.empty)
    empty.text('No items yet — add something!')

    // A long list loads 200 items at a time. This row sits under the last one
    // and is both halves of that: it says more is on the way, and scrolling it
    // into view is what asks for the next page. It's only in the DOM while the
    // server has older items left, so it never lies.
    const more = Div()
    more.addClass(style.more)
    more.text('Loading older items…')

    listEl.el.addEventListener('scroll', () => {
        if (!viewing || !list.hasMoreItems()) return
        const el = listEl.el
        if (el.scrollHeight - el.scrollTop - el.clientHeight > 300) return
        list.loadMore()
    })

    // "Done" heading above the checked zone (shown from 2 done items), with a
    // delete-all-done button. Rebuilt on every render that needs it: baseline's
    // remove()/empty() strip a component's own children permanently, so a reused
    // instance would come back as an empty shell after any render without it.
    let doneDivider: ReturnType<typeof DoneDivider> | null = null
    const buildDivider = () => {
        const divider = DoneDivider()
        divider.on('clear', () => {
            const done = list.getAll().filter(it => it.checked)
            if (!done.length) return
            if (!confirm(`Delete all ${done.length} done items?`)) return
            done.forEach(it => list.remove(it.id))
        })
        divider.on('undo', () => {
            // Non-destructive, so no confirm — toggle every checked item back.
            list.getAll().filter(it => it.checked).forEach(it => list.toggle(it.id))
        })
        return divider
    }

    // Header, add bar, items, then the theme toggle floated bottom-right.
    base.append(header, addBar, listEl, ThemeToggle())

    // --- Drag-to-reorder ---
    // `rows` is shared with the reorder controller: render rebuilds it, the
    // controller reorders it on drop and persists the new order.
    let rows: Row[] = []
    const reorder = createReorder({
        listEl,
        getRows: () => rows,
        setRows: r => { rows = r },
        onReorder: r => {
            // A deliberate drag is the one moment the order is worth sharing:
            // push it to the list row so every member and device follows.
            itemOrder.push(listId, foldDrag(listId, r.map(row => row.item)))
            // endDrag re-appended only the rows, which leaves anything else in
            // listEl stranded above them — send the "loading older items" row
            // back under the last one.
            if (more.el.parentNode) listEl.el.appendChild(more.el)
            // Same for the divider: back above the done zone (the next render
            // re-partitions properly). A drag doesn't change any checked state,
            // so the divider's existence stays valid.
            if (!doneDivider) return
            const firstDone = r.find(row => row.item.checked)
            if (firstDone) listEl.el.insertBefore(doneDivider.el, firstDone.el)
        },
    })

    // Slide the rows up into place one after another, the first `start` ms from
    // now. Counted from the first row in view, so a list reopened scrolled down
    // doesn't keep its visible rows hidden while the ones above take their turn.
    // Inline opacity is cleared, not forced to 1, so a checked row settles at the
    // dimmed `.done` opacity. The "Done" divider fades in with the first done row.
    // The hide must not transition: measuring above has already laid the rows out
    // as visible, so an animated hide plays a fade-out before the fade-in.
    const staggerIn = (start: number) => {
        const top = listEl.el.getBoundingClientRect().top
        const first = Math.max(0, rows.findIndex(r => r.el.getBoundingClientRect().bottom > top))
        const delay = (i: number) => start + Math.max(0, i - first) * 45
        rows.forEach((row, i) => {
            row.style({ opacity: '0', transform: 'translateY(14px)', transition: 'none' })
            row.style({ opacity: '', transform: '', transition: 'opacity 0.3s ease, transform 0.3s ease' }, delay(i))
        })
        if (!doneDivider) return
        doneDivider.style({ opacity: '0', transition: 'none' })
        doneDivider.style({ opacity: '', transition: '' }, delay(rows.findIndex(r => r.item.checked)))
    }

    // --- Render ---
    // Per-item identity + the whole-list signature. A non-animating refresh with
    // the same data (e.g. the server fetch right after the IndexedDB paint) is
    // skipped entirely; anything else is reconciled, not rebuilt.
    const rowSig = (it: Item) => it.id + ':' + (it.checked ? 1 : 0) + ':' + it.name
    const signature = (items: Item[]) => items.map(rowSig).join('|')
    let renderedSig = ''

    // Live rows kept by id so a re-render only touches what changed. We track the
    // last-rendered *name* per row (list.ts mutates item objects in place, so the
    // row's own `item` ref can't detect a change). Checked state is deliberately
    // not tracked here — the row owns it via setChecked, so a toggle reuses the row
    // and animates in place instead of being rebuilt.
    const rowById: Record<string, Row> = {}
    const nameById: Record<string, string> = {}
    const buildRow = (item: Item) => {
        const row = ListItem(item)
        row.on('toggle', () => list.toggle(item.id))
        row.on('delete', () => list.remove(item.id))
        row.on('update', ({ name }: { name: string }) => list.update(item.id, name))
        return row
    }

    const render = (animate = false) => {
        const ordered = sortByOrder(listId, list.getAll())
        const items = doneLast(ordered)

        // The footer is part of what's on screen: the last page arriving empty
        // changes nothing but its presence, and that still has to repaint.
        const sig = signature(items) + (list.hasMoreItems() ? '|more' : '')
        if (!animate && sig === renderedSig) return // unchanged — keep current rows (and any in-flight animation)
        renderedSig = sig

        if (items.length === 0) {
            listEl.empty()
            doneDivider = null
            rows = []
            for (const id of Object.keys(rowById)) { delete rowById[id]; delete nameById[id] }
            listEl.append(empty)
            return
        }
        empty.remove()

        // FLIP step 1 — remember where each current row sits, so any that move
        // (e.g. an item settling into the done zone) glide rather than jump.
        // Skipped during the staggered enter animation.
        const firstTop: Record<string, number> = {}
        if (!animate) for (const id of Object.keys(rowById)) {
            firstTop[id] = rowById[id].el.getBoundingClientRect().top
        }

        // Reconcile by id: reuse a row whose name is unchanged (a toggle just calls
        // setChecked on it, animating in place), rebuild renamed/new ones, drop the
        // gone — then lay them all out in order in one batch. The instant IndexedDB
        // paint stays put when the server refresh matches it.
        const seen = new Set<string>()
        const next: Row[] = []
        items.forEach(item => {
            seen.add(item.id)
            let row = rowById[item.id]
            if (row && nameById[item.id] === item.name) {
                row.setChecked(item.checked) // animates a remote tick; no-ops our own
            } else {
                row?.remove()
                row = buildRow(item)
                rowById[item.id] = row
                nameById[item.id] = item.name
            }
            next.push(row)
        })
        for (const id of Object.keys(rowById)) {
            if (!seen.has(id)) {
                rowById[id].remove()
                delete rowById[id]
                delete nameById[id]
            }
        }
        rows = next
        // Single place that touches the DOM order: re-append every row in order,
        // with the "Done" divider slotted in above the checked zone (items are
        // already partitioned unchecked-first by applyOrder).
        const firstDone = items.findIndex(it => it.checked)
        const doneCount = firstDone === -1 ? 0 : items.length - firstDone
        doneDivider?.el.remove() // plain DOM detach — the instance is discarded
        doneDivider = null
        more.el.remove() // same: _setChildren leaves the DOM alone, so detach first
        listEl._setChildren([])
        if (doneCount < 2) {
            listEl.append(...next)
        } else {
            const divider = buildDivider()
            divider.setCount(doneCount)
            listEl.append(...next.slice(0, firstDone), divider, ...next.slice(firstDone))
            doneDivider = divider
        }
        // Under everything, so scrolling to it is what reaches for the next page.
        if (list.hasMoreItems()) listEl.append(more)

        // Cold open: items load after the open transition (see enter), so the
        // first paint staggers in. A re-open replays the same stagger from enter.
        if (animate) staggerIn(0)

        // FLIP step 2 — invert each moved row to its old spot, then release it to
        // glide to the new one. Clears its inline styles when the glide ends.
        if (!animate) for (const row of next) {
            const top = firstTop[row.item.id]
            if (top === undefined) continue // freshly added — nothing to glide from
            const delta = top - row.el.getBoundingClientRect().top
            if (!delta) continue
            row.el.style.transition = 'none'
            row.el.style.transform = `translateY(${delta}px)`
            requestAnimationFrame(() => {
                row.el.style.transition = 'transform 0.32s cubic-bezier(0.22, 1, 0.36, 1)'
                row.el.style.transform = ''
                const clear = () => { row.el.style.transition = ''; row.el.removeEventListener('transitionend', clear) }
                row.el.addEventListener('transitionend', clear)
            })
        }

        // Keep the local mirror in sync (drops deleted ids, records new ones).
        // The sequence *before* the done partition, so ticking an item doesn't
        // rewrite where it lives — unticking brings it back to its own section.
        // Local-only on purpose: every device derives the same result from the
        // shared order, so only a drag is worth a server write.
        itemOrder.setLocal(listId, ordered.map(it => it.id))
    }

    list.on('change', () => {
        if (list.getListId() !== listId) return // not the active list
        if (reorder.isDragging()) return // don't rebuild mid-drag
        render(animateNext)
        if (animateNext && list.getAll().length > 0) animateNext = false
    })

    base.on('enter', ({ params, data }: IRouteEvent) => {
        const enterId = params.id
        viewing = true

        // Visual setup runs immediately and synchronously so the open morph isn't
        // blocked by auth or data work — that cold first-open work is what made the
        // transition stutter on the native WebView.
        // Match the color of the circle that opened us (--accent drives the colored
        // buttons). Reveal a beat later so the home circle is seen growing in first.
        paintForeground(enterId)
        base.style({ backgroundColor: helpers.surfaceForId(enterId) })
        base.style({ opacity: '1', pointerEvents: 'auto' }, 200)

        listId = params.id
        // Activate the list in the service NOW, not in the deferred load below:
        // an item added before init() ran used to be stamped with the previous
        // list's id (or none) and silently vanish.
        list.setActive(listId)
        const name = data?.name || listsService.getCachedName(listId) || 'List'
        header.setTitle(name)
        if (name === 'List') {
            // Cold start straight into a list (notification tap / deep link):
            // the lists cache hasn't loaded yet, so the title fell back. Fill
            // it in from the local cache as soon as it's read.
            listsService.getLocalLists().then(() => {
                const late = listsService.getCachedName(enterId)
                if (late && viewing && listId === enterId) {
                    header.setTitle(late)
                    ldb.set('list:last', { id: listId, name: late })
                }
            })
        }
        header.hideState() // hide stale state until this list's members load
        header.setPinned(listsService.isPinned(listId)) // ⋮ menu label for this list
        // Remember this as the last page so a fresh app open reopens it directly
        ldb.set('list:last', { id: listId, name })

        // Slide the header and add-bar up on enter, matching the list items below
        ;[header, addBar].forEach((el, i) => {
            el.style({ opacity: '0', transform: 'translateY(14px)', transition: 'opacity 0.3s ease, transform 0.3s ease' })
            el.style({ opacity: '1', transform: 'translateY(0)' }, 230 + i * 70)
        })

        // Focus the add input so the user can type an item straight away — after the
        // open transition has finished, so the keyboard doesn't fight the morph.
        setTimeout(() => {
            if (viewing && listId === enterId) addBar.focus()
        }, 500)

        // Rows persist between visits, and the router caches a page per path, so
        // on a re-open these are this list's own rows with its current content.
        // Replay the stagger on them right away, just after the header (230ms)
        // and add bar (300ms). Don't hold them hidden until the data reload
        // instead: that wait made a re-open look like the list was loading from
        // scratch. A cold open has no rows yet, so its first paint staggers in.
        animateNext = rows.length === 0
        if (!animateNext) staggerIn(370)

        // Defer all data loading (IndexedDB paint + server fetch + members +
        // WebSocket) until the open morph is past its heavy frames, so the cold
        // first-open work doesn't stutter the transition.
        setTimeout(async () => {
            if (!viewing || listId !== enterId) return
            // init paints from IndexedDB immediately, then waits for auth
            // itself before the server fetch (offline that wait can be ~30s,
            // so it must not sit in front of the local paint).
            await list.init(listId)

            // Show whether this list is private / shared by me / shared with me.
            // Derived from membership: the owner is always a member, so > 1 means shared.
            const members = await list.getMembers()
            if (!viewing || listId !== enterId) return // navigated away while loading
            if (members.length === 0) return // fetch failed / offline — leave the pill hidden
            const user = services.supabase.auth.getUser()
            const myRole = members.find(m => m.user_id === user?.id || m.email === user?.email)?.role
            header.setShareState(helpers.shareState(myRole === 'owner' ? 'owner' : 'member', members.length), members.length)
        }, 450)
    })

    base.on('exit', () => {
        viewing = false
        base.style({ opacity: '0', pointerEvents: 'none' })
    })

    return base
}
